import { Add } from '@mui/icons-material';
import { LoadingButton } from '@mui/lab';
import { Alert, Avatar, CircularProgress, Stack, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { setTransportSelectedItem } from 'app/actions/map';
import { setSearchValue, setSelectedItem } from 'app/actions/trailer';
import { IconButton, InputSearch, Scrollbar } from 'app/components';
import { TrailerRoundedIcon } from 'app/icons';
import { TrailerDialog, TrailerForm, TrailerList } from 'app/pages/Trailer';
import { useAsyncAction } from 'app/services';
import { transportLoad } from 'app/store/map';
import { trailerDelete, trailerLoad, trailerUpdate } from 'app/store/trailer';
import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

const Trailer = () => {
  const formRef = useRef();
  const dispatch = useDispatch();
  const { items, selectedItem, searchValue } = useSelector((state) => state.trailer);
  const { transportSelectedItem, transportItems } = useSelector((state) => state.map);
  const [dialogOpen, setDialogOpen] = useState(false);

  const { asyncAction: fetchData, isLoading: isFetchLoading } = useAsyncAction(trailerLoad);
  const {
    asyncAction: updateSubmit,
    isLoading: isUpdateLoading,
    isSuccessful: isUpdateSuccessful,
    isError: isUpdateError,
  } = useAsyncAction(trailerUpdate, [selectedItem]);
  const {
    asyncAction: deleteSubmit,
    isLoading: isDeleteLoading,
    isSuccessful: isDeleteSuccessful,
    isError: isDeleteError,
  } = useAsyncAction(trailerDelete, [selectedItem]);
  const { asyncAction: fetchTransportData, isLoading: isFetchTransportSuccessful } = useAsyncAction(transportLoad);

  const handleDialogOpen = () => {
    setDialogOpen(true);
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
  };

  const handleFormClick = () => {
    formRef.current?.handleFormSubmit();
  };

  const handleFormSubmit = (params) => {
    updateSubmit(selectedItem.id, params);
  };

  // eslint-disable-next-line no-unused-vars
  const handleDeleteClick = () => {
    deleteSubmit(selectedItem.id);
  };

  const handleTrailerSearchChange = (value) => dispatch(setSearchValue(value));

  useEffect(() => {
    if (isUpdateSuccessful) {
      fetchTransportData();
    }
  }, [isUpdateSuccessful]);

  useEffect(() => {
    if (isFetchTransportSuccessful && transportSelectedItem) {
      const updatedTransportItem = transportItems.find((item) => item.id === transportSelectedItem.id);
      dispatch(setTransportSelectedItem(updatedTransportItem));
    }
  }, [transportItems, transportSelectedItem]);

  useEffect(() => {
    fetchData();

    return () => dispatch(setSelectedItem(null));
  }, []);

  return (
    <React.Fragment>
      <Stack
        direction='column'
        sx={(theme) => ({
          width: '500px',
          height: '100%',
          zIndex: '1010',
          overflow: 'hidden',
          bgcolor: 'background.paper',
          borderRight: '1px solid',
          borderColor: theme.palette.divider,
        })}>
        {!isFetchLoading && (
          <React.Fragment>
            <Stack
              direction='row'
              alignItems='center'
              sx={(theme) => ({
                padding: theme.spacing(2),
                '.MuiButton-root.Mui-disabled': {
                  backgroundColor: 'background.default',
                  'svg path': {
                    fill: theme.palette.text.disabled,
                  },
                },
              })}>
              <InputSearch
                placeholder='Госномер, VIN номер или датчик уровня топлива'
                value={searchValue}
                onChange={handleTrailerSearchChange}
              />
              <IconButton onClick={handleDialogOpen} endIcon={<Add />} />
              <TrailerDialog open={dialogOpen} onClose={handleDialogClose} />
            </Stack>
            {items.length && (
              <Stack
                direction='column'
                alignItems='center'
                justifyContent='center'
                sx={{
                  flex: '1 1 auto',
                  overflow: 'hidden',
                }}>
                <TrailerList />
              </Stack>
            )}
          </React.Fragment>
        )}
        {!isFetchLoading && !items.length && (
          <Stack
            direction='column'
            alignItems='center'
            justifyContent='center'
            sx={{
              width: '100%',
              height: '100%',
            }}>
            <Typography align='center' variant='body1' component='div'>
              Здесь пока ничего нет
            </Typography>
            <Typography align='center' color='text.secondary' variant='caption' component='div'>
              В этом списке будут отображаться прицепы
            </Typography>
          </Stack>
        )}
        {isFetchLoading && (
          <Stack
            direction='column'
            alignItems='center'
            justifyContent='center'
            sx={{
              width: '100%',
              height: '100%',
            }}>
            <CircularProgress size={24} />
          </Stack>
        )}
      </Stack>
      <Stack
        direction='column'
        sx={{
          height: '100%',
          flex: '1 1 auto',
        }}>
        {selectedItem && (
          <Scrollbar>
            <Stack
              direction='column'
              alignItems='center'
              sx={(theme) => ({
                maxWidth: '540px',
                margin: 'auto',
                width: '100%',
                padding: theme.spacing(2),
              })}>
              <Stack
                direction='column'
                alignItems='center'
                sx={(theme) => ({
                  marginBottom: theme.spacing(3),
                  '.MuiAvatar-root': {
                    width: '56px',
                    height: '56px',
                    bgcolor: theme.palette.primary.main,
                  },
                  '.MuiAvatar-circular': {
                    color: theme.palette.primary.main,
                    bgcolor: alpha(theme.palette.primary.main, 0.12),
                    svg: {
                      width: '30px',
                      height: '30px',
                    },
                    path: {
                      fill: theme.palette.primary.main,
                    },
                  },
                  '.MuiTypography-root:first-of-type': {
                    marginTop: theme.spacing(1),
                  },
                })}>
                <Avatar>
                  <TrailerRoundedIcon fontSize='large' />
                </Avatar>
                <Typography variant='body1'>{selectedItem.number}</Typography>
              </Stack>
              <Stack
                direction='column'
                alignItems='center'
                sx={(theme) => ({
                  width: '100%',
                  position: 'relative',
                  padding: theme.spacing(2),
                  border: '1px solid',
                  borderRadius: theme.shape.borderRadius,
                  borderColor: theme.palette.divider,
                  bgcolor: 'background.paper',
                  form: {
                    width: '100%',
                  },
                })}>
                <Stack
                  direction='column'
                  sx={(theme) => ({
                    width: '100%',
                    paddingBottom: theme.spacing(1),
                    '.MuiTypography-h6': {
                      fontSize: '15px',
                    },
                    '.MuiTypography-caption': {
                      color: theme.palette.text.secondary,
                    },
                  })}>
                  <Typography variant='h6'>Информация</Typography>
                  <Typography variant='caption'>Основная информация о прицепе</Typography>
                </Stack>
                <TrailerForm ref={formRef} defaultValues={selectedItem} onSubmit={handleFormSubmit} />
                <Stack
                  direction='row'
                  justifyContent='flex-end'
                  sx={(theme) => ({
                    width: '100%',
                    '.MuiAlert-root': {
                      width: '100%',
                      marginTop: theme.spacing(2),
                      borderRadius: theme.shape.borderRadius,
                    },
                  })}>
                  {!isUpdateLoading && isUpdateError && (
                    <Alert severity='error'>
                      Произошла ошибка при изменении данных. Попробуйте снова или обратитесь в техническую поддержку.
                    </Alert>
                  )}
                  {!isUpdateLoading && isUpdateSuccessful && <Alert severity='success'>Данные успешно изменены.</Alert>}
                </Stack>
                <Stack
                  direction='row'
                  justifyContent='flex-end'
                  sx={(theme) => ({
                    width: '100%',
                    paddingTop: theme.spacing(3),
                  })}>
                  <LoadingButton variant='contained' loading={isUpdateLoading} onClick={handleFormClick} disableElevation>
                    Сохранить изменения
                  </LoadingButton>
                </Stack>
              </Stack>
              <Stack
                direction='column'
                alignItems='center'
                sx={(theme) => ({
                  width: '100%',
                  display: 'none',
                  position: 'relative',
                  marginTop: theme.spacing(3),
                  padding: theme.spacing(2),
                  border: '1px solid',
                  borderRadius: theme.shape.borderRadius,
                  borderColor: theme.palette.divider,
                  bgcolor: 'background.paper',
                  '.MuiAlert-root': {
                    marginTop: theme.spacing(0),
                    marginBottom: theme.spacing(3),
                  },
                })}>
                <Stack
                  direction='column'
                  sx={(theme) => ({
                    width: '100%',
                    paddingBottom: theme.spacing(3),
                    '.MuiTypography-h6': {
                      fontSize: '15px',
                    },
                    '.MuiTypography-caption': {
                      color: theme.palette.text.secondary,
                    },
                  })}>
                  <Typography variant='h6'>Удалить прицеп</Typography>
                  <Typography variant='caption'>
                    Можно удалить данный прицеп, но это действие необратимо. Все прикрепленные геозоны будут удалены из данной группы.
                    Пожалуйста, будьте уверены, что Вы хотите это сделать
                  </Typography>
                </Stack>
                <Stack
                  direction='row'
                  justifyContent='flex-end'
                  sx={(theme) => ({
                    width: '100%',
                    '.MuiAlert-root': {
                      width: '100%',
                      marginTop: theme.spacing(1),
                      borderRadius: theme.shape.borderRadius,
                    },
                  })}>
                  {isDeleteError && (
                    <Alert severity='error'>
                      Произошла ошибка при удалении данных. Попробуйте снова или обратитесь в техническую поддержку.
                    </Alert>
                  )}
                  {isDeleteSuccessful && <Alert severity='success'>Данные успешно изменены.</Alert>}
                </Stack>
                <Stack
                  direction='row'
                  justifyContent='flex-end'
                  sx={(theme) => ({
                    width: '100%',
                    paddingTop: theme.spacing(0),
                  })}>
                  <LoadingButton color='error' variant='contained' loading={isDeleteLoading} disableElevation>
                    Удалить прицеп
                  </LoadingButton>
                </Stack>
              </Stack>
            </Stack>
          </Scrollbar>
        )}
        {!selectedItem && (
          <Stack
            direction='column'
            alignItems='center'
            justifyContent='center'
            sx={(theme) => ({
              height: '100%',
              padding: theme.spacing(0, 2),
              '.MuiTypography-root': {
                borderRadius: '20px',
                marginTop: '24px',
                padding: theme.spacing(0.5, 2),
                bgcolor: alpha(theme.palette.grey.main, 0.12),
              },
            })}>
            <Typography align='center' color='text.secondary' variant='caption' component='label'>
              Выберите прицеп из списка
            </Typography>
          </Stack>
        )}
      </Stack>
    </React.Fragment>
  );
};

export default Trailer;
